// OrganismDisplayViewEduMealPreviewDesktop.jsx
// 데스크탑 전용 교육 급식 콘텐츠 미리보기 컴포넌트

import React, { useEffect, useState, useRef, useCallback } from "react";
import styled from "styled-components";
import { toPng, toSvg } from "html-to-image";
import { palette } from "../../../../assets/styles/Palette";
import { Body3 } from "../../../../assets/styles/Typography";
import { Button } from "../../../../assets/styles/ButtonStyle";
import MoleculeSizeArea from "../molecules/MoleculeSizeArea";
import MoleculeSizeModeArea from "../molecules/MoleculeSizeModeArea";
import MoleculeMenuBoard from "../../../CreateEducationContentPage/components/molecules/MoleculeMenuBoard";
import { MoleculeMenuBoardKiosk } from "../../../MoleculeMenuBoardKiosk";

const OrganismDisplayViewEduMealPreviewDesktop = ({
  template,
  onClose,
  title,
  mealData,
  noticeText,
  displayWidth,
  displayHeight,
}) => {
  console.log("🖥️ 데스크탑 전용 급식 미리보기 컴포넌트");

  const [displayMode, setDisplayMode] = useState("web"); // "web" | "kiosk"
  const previewRef = useRef(null);
  const downloadRef = useRef(null); // PNG 생성용 별도 ref

  const [originalSize, setOriginalSize] = useState({
    width: 3840,
    height: 2160,
  });

  const [previewSize, setPreviewSize] = useState({
    width: 800,
    height: 450,
  });

  // displayWidth와 displayHeight가 변경될 때 원본 사이즈 업데이트
  useEffect(() => {
    console.log(
      "데스크탑 급식 미리보기 - DisplayWidth:",
      displayWidth,
      "DisplayHeight:",
      displayHeight
    );
    if (displayWidth && displayHeight) {
      const width = parseInt(displayWidth);
      const height = parseInt(displayHeight);

      setOriginalSize({
        width: width,
        height: height,
      });

      // 화면에 맞는 미리보기 사이즈 계산 (화면을 완전히 채우도록)
      const maxWidth = window.innerWidth;
      const maxHeight = window.innerHeight - 120; // 헤더와 사이즈 영역 높이 고려

      // 화면을 완전히 채우도록 설정 (비율 무시)
      setPreviewSize({
        width: maxWidth,
        height: maxHeight,
      });
    }
  }, [displayWidth, displayHeight]);

  // 창 크기 변경시 미리보기 사이즈 재계산
  useEffect(() => {
    const handleResize = () => {
      const maxWidth = window.innerWidth;
      const maxHeight = window.innerHeight - 120; // 헤더와 사이즈 영역 높이 고려

      // 화면을 완전히 채우도록 설정 (비율 무시)
      setPreviewSize({
        width: maxWidth,
        height: maxHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [originalSize]);

  const handleDownloadPng = useCallback(() => {
    if (downloadRef.current === null) {
      return;
    }

    // downloadRef 요소를 임시로 보이게 만들기
    const downloadElement = downloadRef.current;
    downloadElement.style.position = "fixed";
    downloadElement.style.top = "0";
    downloadElement.style.left = "0";
    downloadElement.style.zIndex = "10000";

    // 약간의 지연을 두어 렌더링 완료 후 캡처
    setTimeout(() => {
      const targetWidth = displayMode === "kiosk" ? 1080 : originalSize.width;
      const targetHeight = displayMode === "kiosk" ? 1920 : originalSize.height;
      
      toPng(downloadElement, {
        cacheBust: true,
        width: targetWidth,
        height: targetHeight,
        style: {
          transform: "none",
          width: `${targetWidth}px`,
          height: `${targetHeight}px`,
        },
      })
        .then((dataUrl) => {
          const link = document.createElement("a");
          link.download = `${title || "template"}.png`;
          link.href = dataUrl;
          link.click();

          // 다시 숨기기
          downloadElement.style.position = "absolute";
          downloadElement.style.top = "-9999px";
          downloadElement.style.left = "-9999px";
          downloadElement.style.zIndex = "-1";
        })
        .catch((err) => {
          console.log("데스크탑 급식 미리보기 PNG 생성 오류:", err);

          // 에러 시에도 다시 숨기기
          downloadElement.style.position = "absolute";
          downloadElement.style.top = "-9999px";
          downloadElement.style.left = "-9999px";
          downloadElement.style.zIndex = "-1";
        });
    }, 100);
  }, [originalSize, title]);

  const handleDownloadSvg = useCallback(() => {
    if (downloadRef.current === null) {
      return;
    }

    // downloadRef 요소를 임시로 보이게 만들기
    const downloadElement = downloadRef.current;
    downloadElement.style.position = "fixed";
    downloadElement.style.top = "0";
    downloadElement.style.left = "0";
    downloadElement.style.zIndex = "10000";

    // 약간의 지연을 두어 렌더링 완료 후 캡처
    setTimeout(() => {
      const targetWidth = displayMode === "kiosk" ? 1080 : originalSize.width;
      const targetHeight = displayMode === "kiosk" ? 1920 : originalSize.height;
      
      toSvg(downloadElement, {
        cacheBust: true,
        width: targetWidth,
        height: targetHeight,
        style: {
          transform: "none",
          width: `${targetWidth}px`,
          height: `${targetHeight}px`,
        },
      })
        .then((dataUrl) => {
          const link = document.createElement("a");
          link.download = `${title || "template"}.svg`;
          link.href = dataUrl;
          link.click();

          // 다시 숨기기
          downloadElement.style.position = "absolute";
          downloadElement.style.top = "-9999px";
          downloadElement.style.left = "-9999px";
          downloadElement.style.zIndex = "-1";
        })
        .catch((err) => {
          console.log("데스크탑 급식 미리보기 SVG 생성 오류:", err);

          // 에러 시에도 다시 숨기기
          downloadElement.style.position = "absolute";
          downloadElement.style.top = "-9999px";
          downloadElement.style.left = "-9999px";
          downloadElement.style.zIndex = "-1";
        });
    }, 100);
  }, [originalSize, title]);

  // 디스플레이 모드 변경 핸들러
  const handleModeChange = useCallback((mode) => {
    console.log("🔄 급식 미리보기: 디스플레이 모드 변경:", mode);
    setDisplayMode(mode);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);

    // 전체화면 진입 시 body 스크롤 비활성화
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    console.log("🖥️ 데스크탑 급식 미리보기: 전체화면 모드 진입");
    const enterFullscreen = async () => {
      try {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        } else if (document.documentElement.webkitRequestFullscreen) {
          await document.documentElement.webkitRequestFullscreen();
        } else if (document.documentElement.msRequestFullscreen) {
          await document.documentElement.msRequestFullscreen();
        }
      } catch (error) {
        console.log("데스크탑 급식 미리보기: 전체화면 모드 진입 실패:", error);
      }
    };

    enterFullscreen();

    // 컴포넌트 언마운트 시 스크롤 복원
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      onClose();
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    const handleFullscreenChange = () => {
      // 전체화면이 해제되면 미리보기도 종료
      if (!document.fullscreenElement) {
        onClose();
      }
    };

    window.addEventListener("popstate", handlePopState);
    window.addEventListener("keydown", handleKeyDown);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "msfullscreenchange",
        handleFullscreenChange
      );
    };
  }, [onClose]);

  const handleGoBack = () => {
    // 전체화면 모드 해제
    const exitFullscreen = async () => {
      try {
        if (document.fullscreenElement) {
          if (document.exitFullscreen) {
            await document.exitFullscreen();
          } else if (document.webkitExitFullscreen) {
            await document.webkitExitFullscreen();
          } else if (document.msExitFullscreen) {
            await document.msExitFullscreen();
          }
        }
      } catch (error) {
        console.log("데스크탑 급식 미리보기: 전체화면 모드 해제 실패:", error);
      }
    };

    exitFullscreen().then(() => {
      onClose();
    });
  };

  // 미리보기용 컨텐츠 (화면을 완전히 채우도록)
  const renderPreviewContent = () => {
    return (
      <div
        ref={previewRef}
        style={{
          width: previewSize.width,
          height: previewSize.height,
          transformOrigin: "top left",
          overflow: "hidden",
          display: "flex",
          justifyContent: displayMode === "kiosk" ? "center" : "flex-start",
          alignItems: displayMode === "kiosk" ? "center" : "flex-start",
        }}
      >
        <div
          style={{
            width: displayMode === "kiosk" ? 1080 : originalSize.width,
            height: displayMode === "kiosk" ? 1920 : originalSize.height,
            transform: `scale(${
              displayMode === "kiosk"
                ? Math.min(
                    previewSize.width / 1080,
                    previewSize.height / 1920
                  )
                : Math.min(
                    previewSize.width / originalSize.width,
                    previewSize.height / originalSize.height
                  )
            })`,
            transformOrigin: displayMode === "kiosk" ? "center center" : "top left",
          }}
        >
          {displayMode === "kiosk" ? (
            <MoleculeMenuBoardKiosk
              backgroundImage="healthy"
              menuData={mealData}
              noticeText={noticeText || "키오스크형 급식 디스플레이"}
            />
          ) : (
            <MoleculeMenuBoard
              backgroundImage={template?.image}
              menuData={mealData}
              noticeText={noticeText}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <PreviewContainer>
      <Header>
        <TitleContainer>
          <TemplateCategory>
            <Body3 color="primary" style={{ fontSize: "12px" }}>
              {template?.category || "급식 메뉴"} (데스크탑)
            </Body3>
          </TemplateCategory>
          <HeaderTitle>{title || "급식 미리보기"} - 데스크탑</HeaderTitle>
        </TitleContainer>
        <ButtonGroup>
          <Button Large onClick={handleDownloadPng}>
            PNG로 저장
          </Button>
          <Button Large onClick={handleDownloadSvg}>
            SVG로 저장
          </Button>
          <Button Primary Large Fill onClick={handleGoBack}>
            미리보기 종료하기
          </Button>
        </ButtonGroup>
      </Header>

      <StyledSizeAreaContainer>
        <MoleculeSizeArea
          title="내 디스플레이 (데스크탑)"
          width={displayWidth?.toString() || originalSize.width.toString()}
          height={displayHeight?.toString() || originalSize.height.toString()}
        />
        {/* <MoleculeSizeModeArea
          width={displayWidth?.toString() || originalSize.width.toString()}
          height={displayHeight?.toString() || originalSize.height.toString()}
          initialMode={displayMode}
          onModeChange={handleModeChange}
        /> */}

      </StyledSizeAreaContainer>

      <PreviewContent
        onContextMenu={(e) => {
          e.preventDefault();
          // 컨텍스트 메뉴 대신 간단한 알림이나 동작
          const action = window.confirm(
            "미리보기를 종료하시겠습니까?\n확인: 종료\n취소: PNG 저장"
          );
          if (action) {
            handleGoBack();
          } else {
            handleDownloadPng();
          }
        }}
      >
        {renderPreviewContent()}
      </PreviewContent>

      {/* PNG/SVG 생성용 숨겨진 원본 사이즈 요소 */}
      <div
        ref={downloadRef}
        style={{
          position: "absolute",
          top: "-9999px",
          left: "-9999px",
          width: displayMode === "kiosk" ? 1080 : originalSize.width,
          height: displayMode === "kiosk" ? 1920 : originalSize.height,
          background: "white",
          zIndex: -1,
        }}
      >
        {displayMode === "kiosk" ? (
          <MoleculeMenuBoardKiosk
            backgroundImage="healthy"
            menuData={mealData}
          />
        ) : (
          <MoleculeMenuBoard
            backgroundImage={template?.image}
            menuData={mealData}
            noticeText={noticeText}
          />
        )}
      </div>
    </PreviewContainer>
  );
};

export default OrganismDisplayViewEduMealPreviewDesktop;

// 데스크탑 전용 스타일 컴포넌트들
const PreviewContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: #ebebeb;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 3px solid #ff9800; /* 급식 구분용 오렌지 테두리 */
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const Header = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 7px 14px;
  background: linear-gradient(
    135deg,
    #fff3e0,
    #ffe0b2
  ); /* 급식 구분용 오렌지 그라데이션 */
  border-bottom: 2px solid #ff9800; /* 급식 구분용 오렌지 테두리 */
  flex-shrink: 0;
`;

const TitleContainer = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${palette.gray800};
`;

const PreviewContent = styled.div`
  flex: 1;
  overflow: hidden;
  padding: 0;
  margin: 0;
  background: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const TemplateCategory = styled.div`
  color: ${palette.primary};
  font-weight: 500;
  background-color: rgba(255, 152, 0, 0.1);
  padding: 3px 8px;
  border-radius: 2px;
  border: 1px solid #ff9800; /* 급식 구분용 오렌지 테두리 */
`;

const StyledSizeAreaContainer = styled.div`
  top: 56px;
  left: 0;
  right: 0;
  z-index: 99;
  background: rgba(255, 152, 0, 0.05); /* 급식 구분용 오렌지 배경 */
`;
